import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ReductionModal from "../components/ReductionModal";
import { TimeSheet, MessageSheet } from "../components/ParentSheets";
import { KidAvatar, StatusPill, hm, kidColour, kidStatus } from "../lib/kidLook";
import { toast } from "sonner";
import { Plus, Pause, Play, ChevronRight, Minus, DollarSign, UserPlus, Check, X, Copy, Mail, MessageCircle, MessagesSquare } from "lucide-react";

const EMPTY_IMG = "/img/yoda-hero.png";

const AVATARS = [
  "https://images.pexels.com/photos/7145957/pexels-photo-7145957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
  "https://images.pexels.com/photos/19604289/pexels-photo-19604289.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
];

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", weekly_hours_pool: 20, reset_weekday: 0, avatar_url: AVATARS[0], state: "NSW" });
  const [reduction, setReduction] = useState(null);
  const [timeFor, setTimeFor] = useState(null);
  const [messageFor, setMessageFor] = useState(null);
  const [waiting, setWaiting] = useState([]);
  const [cashouts, setCashouts] = useState([]);
  const [inviteLink, setInviteLink] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);

  const resendVerify = async () => {
    setVerifyBusy(true); setVerifyMsg("");
    try {
      await api.post("/auth/resend-verification");
      setVerifyMsg("Verification email sent. Check your inbox.");
      refresh?.();
    } catch (e) {
      setVerifyMsg(formatApiErrorDetail(e.response?.data?.detail) || "Try again in a moment.");
    } finally { setVerifyBusy(false); }
  };

  const load = async () => {
    try {
      const [kR, cR, aR] = await Promise.all([
        api.get("/kids"), api.get("/cashout-requests?status=pending"),
        api.get("/approvals?status=pending").catch(() => ({ data: [] })),
      ]);
      setKids(kR.data);
      setCashouts(cR.data);
      setWaiting(aR.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const togglePause = async (kid) => {
    try { await api.post(`/kids/${kid.id}/${kid.paused ? "resume" : "pause"}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const addKid = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/kids", form);
      setShowAdd(false);
      setForm({ name: "", weekly_hours_pool: 20, reset_weekday: 0, avatar_url: AVATARS[0], state: "NSW" });
      // Jump straight into the per-day schedule editor for the new kid.
      if (data?.id) {
        navigate(`/kids/${data.id}`);
      } else {
        load();
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const approveCashout = async (id) => { await api.post(`/cashout-requests/${id}/approve`); load(); };
  const denyCashout = async (id) => { await api.post(`/cashout-requests/${id}/deny`); load(); };

  const createInvite = async () => {
    setInviteBusy(true);
    try {
      const { data } = await api.post("/family/invite", inviteEmail ? { email: inviteEmail } : {});
      const url = data.url || `${window.location.origin}/invite?token=${data.token}`;
      setInviteLink(url);
      setInviteSent(!!data.emailed);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setInviteBusy(false); }
  };

  const openInvite = () => {
    setInviteLink(""); setInviteSent(false); setInviteEmail("");
    setInviteOpen(true);
  };

  const copyInvite = async () => {
    try { await navigator.clipboard.writeText(inviteLink); } catch (_) { /* ignore */ }
  };

  return (
    <div data-testid="dashboard-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">Family</h1>
          <div className="text-sm text-[#707A6E]">
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </div>
      </div>

      {waiting.length > 0 && (
        <Link to="/approvals" data-testid="requests-banner"
              className="mb-3 flex items-center gap-3 rounded-[18px] border border-[#F3DFAE] bg-[#FDF1D8] px-4 py-3">
          <span className="text-xl">🙋</span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-[#2C342A]">
              {waiting.length} request{waiting.length === 1 ? "" : "s"} waiting
            </div>
            <div className="truncate text-xs text-[#707A6E]">Tap to say yes or no</div>
          </div>
          <ChevronRight size={18} className="text-[#707A6E]" />
        </Link>
      )}

      {error && (
        <div data-testid="dashboard-error" className="mb-6 text-sm text-[#C06E52] bg-[#C06E52]/10 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {user && user.email_verified === false && (
        <div className="mb-6 card-soft p-4 border-l-4 border-[#D99A2B] flex items-start gap-3" data-testid="verify-banner">
          <Mail size={18} className="text-[#D99A2B] mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-[#2C342A]">
              <b>Verify your email</b> to receive invites, cash-out alerts and the weekly recap. We sent a link to <span className="font-mono">{user.email}</span>.
            </div>
            {verifyMsg && <div className="text-xs text-[#707A6E] mt-1" data-testid="verify-msg">{verifyMsg}</div>}
          </div>
          <button onClick={resendVerify} disabled={verifyBusy} data-testid="resend-verification-btn"
                  className="shrink-0 rounded-xl bg-[#D99A2B] text-white px-3 py-2 text-sm font-medium hover:bg-[#A87414] disabled:opacity-50">
            {verifyBusy ? "Sending…" : "Resend"}
          </button>
        </div>
      )}

      {cashouts.length > 0 && (
        <div className="card-soft p-6 mb-8 border-l-4 border-[#D99A2B]" data-testid="cashout-panel">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign size={18} className="text-[#D99A2B]" />
            <h3 className="font-heading text-lg font-semibold text-[#2C342A]">
              Cash-out requests <span className="text-sm text-[#707A6E] font-normal">({cashouts.length} pending)</span>
            </h3>
          </div>
          <div className="space-y-2">
            {cashouts.map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-[#F5F4F0] rounded-xl px-4 py-3" data-testid={`cashout-req-${c.id}`}>
                <div>
                  <div className="font-medium text-[#2C342A]">{c.kid_name} wants <span className="font-mono">${c.amount.toFixed(2)}</span></div>
                  {c.note && <div className="text-xs text-[#707A6E] mt-0.5">"{c.note}"</div>}
                  <div className="text-[10px] text-[#707A6E] mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approveCashout(c.id)} data-testid={`cashout-approve-${c.id}`}
                          className="inline-flex items-center gap-1 rounded-xl bg-[#5C7A4F] text-white px-3 py-2 text-sm font-medium hover:bg-[#4B6640]">
                    <Check size={14} /> Approve
                  </button>
                  <button onClick={() => denyCashout(c.id)} data-testid={`cashout-deny-${c.id}`}
                          className="inline-flex items-center gap-1 rounded-xl border border-[#C06E52] text-[#C06E52] px-3 py-2 text-sm font-medium hover:bg-[#C06E52]/10">
                    <X size={14} /> Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#707A6E]">Loading family…</div>
      ) : kids.length === 0 ? (
        <div data-testid="empty-state" className="card-soft p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl font-semibold text-[#2C342A]">Start by adding your first kid</h2>
            <p className="text-[#707A6E] mt-3">
              Set daily hours, an available window per day, and let holidays run their own allocations.
            </p>
            <button data-testid="empty-add-kid-btn" onClick={() => setShowAdd(true)}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#5C7A4F] text-white px-5 py-3 font-medium hover:bg-[#4B6640]">
              <Plus size={18} /> Add your first kid
            </button>
          </div>
          <img src={EMPTY_IMG} alt="" className="rounded-2xl w-full object-cover" />
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-testid="kids-grid">
          {kids.map((kid) => {
            const pol = kid.policy || {};
            const total = Math.max(1, pol.total_today_seconds || 0);
            const used = pol.used_today_seconds || 0;
            const left = pol.remaining_today_seconds || 0;
            const st = kidStatus(kid);
            const outOfTime = pol.shielded && left <= 0 && !kid.paused;
            const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };
            return (
              <Link key={kid.id} to={`/kids/${kid.id}`} data-testid={`kid-card-${kid.id}`}
                    className="card-soft card-soft-hover block p-4">
                <div className="flex items-center gap-3">
                  <KidAvatar name={kid.name} colour={kidColour(kid.id, kids)} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[18px] font-semibold text-[#2C342A]">{kid.name}</div>
                    <div className="mt-0.5" data-testid={`kid-status-${kid.id}`}><StatusPill status={st} /></div>
                  </div>
                  <div className="text-right">
                    <div className="tabular text-[26px] font-bold leading-tight tracking-tight text-[#2C342A]"
                         data-testid={`kid-left-${kid.id}`}>{hm(left)}</div>
                    <div className="text-[12.5px] text-[#707A6E]" data-testid={`kid-used-${kid.id}`}>
                      {left > 0 ? "left today" : `used ${hm(used)} of ${hm(total)}`}
                    </div>
                  </div>
                </div>
                <div className="mb-1.5 mt-3 h-2 overflow-hidden rounded-full bg-[#EEECE6]">
                  <i className="block h-full rounded-full"
                     style={{ width: `${Math.min(100, (used / total) * 100)}%`, background: st.bar }} />
                </div>
                <div className="mt-3 flex gap-2">
                  {kid.paused ? (
                    <button data-testid={`kid-toggle-pause-${kid.id}`} onClick={stop(() => togglePause(kid))}
                            className="btn-solid flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm">
                      <Play size={14} /> Resume
                    </button>
                  ) : outOfTime ? (
                    <button data-testid={`kid-give-time-${kid.id}`} onClick={stop(() => setTimeFor(kid))}
                            className="btn-solid flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm">
                      <Plus size={14} /> Give time
                    </button>
                  ) : (
                    <>
                      <button data-testid={`kid-toggle-pause-${kid.id}`} onClick={stop(() => togglePause(kid))}
                              className="btn-soft flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm">
                        <Pause size={14} className="text-[#E0A33A]" fill="currentColor" /> Pause
                      </button>
                      <button data-testid={`kid-give-time-${kid.id}`} onClick={stop(() => setTimeFor(kid))}
                              className="btn-soft flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm">
                        <Plus size={14} /> Time
                      </button>
                    </>
                  )}
                  <button data-testid={`kid-message-${kid.id}`} onClick={stop(() => setMessageFor([kid.id]))}
                          aria-label={`Message ${kid.name}`}
                          className="btn-soft flex flex-1 items-center justify-center py-2.5 text-sm">
                    <MessageCircle size={16} />
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
        {kids.length > 1 && (
          <button onClick={() => setMessageFor(kids.map((k) => k.id))} data-testid="message-everyone"
                  className="btn-soft mt-3 flex w-full items-center justify-center gap-2 py-3 text-sm md:w-auto md:px-6">
            <MessagesSquare size={16} /> Message everyone
          </button>
        )}
        <div className="mt-6 flex flex-wrap gap-2 border-t border-[#E4E2DC] pt-5">
          <button data-testid="add-kid-btn" onClick={() => setShowAdd(true)}
                  className="btn-soft inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm">
            <Plus size={16} /> Add a kid
          </button>
          <button data-testid="reduction-open-btn" onClick={() => setReduction({ defaultKidId: null })}
                  disabled={kids.length === 0}
                  className="btn-soft inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm disabled:opacity-40">
            <Minus size={16} /> Take time from several
          </button>
          <button data-testid="invite-coparent-btn" onClick={openInvite}
                  className="btn-soft inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm">
            <UserPlus size={16} /> Invite co-parent
          </button>
        </div>
        </>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm flex items-center justify-center p-6" data-testid="add-kid-modal">
          <form onSubmit={addKid} className="card-soft p-8 w-full max-w-md fade-in-up" data-testid="add-kid-form">
            <h3 className="font-heading text-2xl font-semibold text-[#2C342A]">Add a kid</h3>
            <p className="text-[#707A6E] text-sm mt-1">You'll set per-day hours right after creating them.</p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Name</label>
                <input data-testid="add-kid-name-input" value={form.name} required
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">State (for holidays)</label>
                <select data-testid="add-kid-state-select" value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                        className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]">
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Reset day</label>
                <select data-testid="add-kid-resetday-select" value={form.reset_weekday}
                        onChange={(e) => setForm({ ...form, reset_weekday: parseInt(e.target.value) })}
                        className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]">
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Avatar</label>
                <div className="mt-2 flex gap-2">
                  {AVATARS.map((src) => (
                    <button type="button" key={src} onClick={() => setForm({ ...form, avatar_url: src })}
                            className={`h-14 w-14 rounded-2xl overflow-hidden border-2 ${form.avatar_url === src ? "border-[#5C7A4F]" : "border-transparent"}`}>
                      <img src={src} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button type="button" onClick={() => setShowAdd(false)}
                      className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 font-medium text-[#2C342A] hover:bg-[#F0EFEB]"
                      data-testid="add-kid-cancel-btn">
                Cancel
              </button>
              <button type="submit"
                      className="flex-1 rounded-xl bg-[#5C7A4F] text-white px-4 py-3 font-medium hover:bg-[#4B6640]"
                      data-testid="add-kid-submit-btn">
                Add kid
              </button>
            </div>
          </form>
        </div>
      )}

      {inviteOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" data-testid="invite-modal">
          <div className="card-soft p-8 w-full max-w-md fade-in-up">
            <div className="text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5C7A4F]/10 text-[#5C7A4F] mb-4">
                <UserPlus size={26} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-[#2C342A]">Invite co-parent</h3>
              <p className="text-[#707A6E] text-sm mt-2">
                We'll email them the invite from <span className="font-mono">notifications@yoda.watch</span>. They'll set their own password and instantly see your kids, rules, tokens and cash-outs.
              </p>
            </div>
            {!inviteLink ? (
              <div className="mt-6 space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Their email (optional)</label>
                  <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                         placeholder="dad@example.com"
                         data-testid="invite-email-input"
                         className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]" />
                  <p className="text-[10px] text-[#707A6E] mt-1">Leave blank to just get a link to share.</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setInviteOpen(false)} className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 font-medium" data-testid="invite-close-btn">Cancel</button>
                  <button onClick={createInvite} disabled={inviteBusy} className="flex-1 rounded-xl bg-[#5C7A4F] text-white px-4 py-3 font-medium hover:bg-[#4B6640] disabled:opacity-50" data-testid="invite-send-btn">
                    {inviteBusy ? "Creating…" : (inviteEmail ? "Send invite" : "Get link")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                {inviteSent && (
                  <div className="mb-3 text-sm bg-emerald-500/10 text-emerald-800 rounded-xl px-3 py-2" data-testid="invite-sent-msg">
                    Invite emailed to <b>{inviteEmail}</b>.
                  </div>
                )}
                <div className="flex gap-2">
                  <input readOnly value={inviteLink} className="flex-1 rounded-xl border border-[#E4E2DC] bg-[#F0EFEB] px-3 py-2 text-sm font-mono" data-testid="invite-link-input" />
                  <button onClick={copyInvite} className="rounded-xl bg-[#5C7A4F] text-white px-3 hover:bg-[#4B6640]" data-testid="invite-copy-btn">
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-xs text-[#707A6E] mt-3 text-center">Expires in 14 days.</p>
                <button onClick={() => setInviteOpen(false)} className="mt-6 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 font-medium" data-testid="invite-done-btn">
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {reduction && (
        <ReductionModal kids={kids} defaultKidId={reduction.defaultKidId}
                        onClose={(applied) => { setReduction(null); if (applied) load(); }} />
      )}

      {timeFor && (
        <TimeSheet kid={timeFor} onClose={() => setTimeFor(null)} onDone={(m) => { toast.success(m); load(); }} />
      )}

      {messageFor && (
        <MessageSheet kids={kids} initialKidIds={messageFor} onClose={() => setMessageFor(null)}
                      onDone={(m) => { toast.success(m); load(); }} />
      )}
    </div>
  );
}
